import React, { useState } from "react";
import { Button } from "../../../design/button";
import { httpsCallable } from "firebase/functions";
import { doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { functions, db } from "../../../firebase";
import { ArrowPathIcon } from "@heroicons/react/16/solid";

interface RegeneratePDFButtonProps {
  visitId: string;
  onSuccess?: (storagePath: string) => void;
}

interface RegeneratePDFResponse {
  success: boolean;
  storagePath: string;
  message: string;
}

const RegeneratePDFButton: React.FC<RegeneratePDFButtonProps> = ({
  visitId,
  onSuccess,
}) => {
  const [isRegenerating, setIsRegenerating] = useState(false);

  const handleRegenerate = async () => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm("Are you sure you want to regenerate the PDF? This will replace the existing event report PDF.")) {
      return;
    }

    setIsRegenerating(true);
    try {
      const result = await httpsCallable<{ visitId: string }, RegeneratePDFResponse>(
        functions,
        "regenerateVisitPDF"
      )({ visitId });

      if (result.data.success) {
        // Update the visit document with the new eventReportPath
        // This will trigger the onSnapshot listener and automatically refresh the UI
        const visitRef = doc(db, "visits", visitId);
        await updateDoc(visitRef, {
          eventReportPath: result.data.storagePath,
          eventReportUpdatedAt: serverTimestamp(),
        });

        window.alert("PDF regenerated successfully!");
        onSuccess?.(result.data.storagePath);
      }
    } catch (error: unknown) {
      console.error("Error regenerating PDF:", error);
      const message = error instanceof Error ? error.message : "Unknown error occurred";
      window.alert(`Failed to regenerate PDF: ${message}`);
    } finally {
      setIsRegenerating(false);
    }
  };

  return (
    <Button
      outline
      onClick={handleRegenerate}
      disabled={isRegenerating}
      className="flex items-center gap-2"
    >
      <ArrowPathIcon className={`w-4 h-4 ${isRegenerating ? "animate-spin" : ""}`} />
      {isRegenerating ? "Regenerating..." : "Regenerate PDF"}
    </Button>
  );
};

export default RegeneratePDFButton;
